import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Loader2, Mail, Lock, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success("Welcome, admin.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-[#060810] relative overflow-hidden">
      <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 50% 0%, rgba(34,211,238,0.12), transparent 60%)" }} />
      <div className="w-full max-w-md fade-up relative z-10" data-testid="admin-login-page">
        <div className="mb-8 flex flex-col items-center text-center">
          <Logo size={44} />
          <div className="mt-6 w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white mt-4">Admin access</h1>
          <p className="text-slate-400 mt-2 text-sm">Restricted area — administrators only.</p>
        </div>

        <div className="glass neon-border rounded-2xl p-6">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Admin email</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Mail className="w-4 h-4 text-slate-500" />
                <input data-testid="admin-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@carivisor.com" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Password</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Lock className="w-4 h-4 text-slate-500" />
                <input data-testid="admin-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <button data-testid="admin-login-submit" disabled={loading}
              className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4" /> Enter dashboard</>}
            </button>
          </form>
        </div>
        <p className="text-xs text-slate-600 text-center mt-6">Carivisor · Administrative console</p>
      </div>
    </div>
  );
}
